import { is, path, isEmpty, dissocPath } from 'ramda'
import moment from 'moment'
import { camelCase } from './change-case'
import { BUSINESS_STATUSES, DATE_FORMAT } from '@/lib/consts'

export function __t (s: string): string {
  return s
}

/*
 * converts list of names to object, similar
 * to TypeScript enum
 * eg. ['a', 'b'] produces: { a: 'a', b: 'b' }
 * @param Array<string> names
 * @returns { [key:string]: string } enum object (frozen)
 */
export function makeEnum<T extends string> (names: T[]): Readonly<Record<T, T>> {
  return Object.freeze(
    names.reduce((o, name) => {
      o[name] = name
      return o
    }, {} as Record<T, T>)
  )
}

/*
 * deletes object prop. by path & its
 * empty ancestors
 *
 * example:
 *   delWithClear(
 *     { a: { b: { c: 1 } }, d: 2 },
 *     'a', 'b', 'c'
 *   )
 *   { d: 2 )
 */
export function delWithClear (o: Record<string, unknown>, ...p: string[]): Record<string, unknown> {
  let _o = dissocPath(p, o)

  p.pop()

  while (p.length) {
    const t = path(p, _o)
    if (!isEmpty(t)) { break }
    _o = dissocPath(p, _o)
    p.pop()
  }

  return _o as Record<string, unknown>
}

export function distinct (o: unknown, ...paths: string[][]): unknown {
  let v = null
  if (is(Object, o)) {
    for (const p of paths) {
      v = path(p, o as Record<string, unknown>)
      if (v) { break }
    }
  }
  return v
}

export { debounce } from '@/functions/utils/debounce'

interface FormError {
  field?: string
  description?: string
  code?: string
}

export function hasFormErrors (errors: FormError[] | null | undefined, form: Record<string, unknown>): boolean {
  if (!errors) return false
  let hasErrors = false

  for (const error of errors) {
    if (error.field && Object.keys(form).includes(error.field?.split('.')[0])) {
      hasErrors = true
      break
    }
  }

  return hasErrors
}

interface CurrencyFormat {
  pos: string
  neg: string
  zero: string
}

interface AccountingConfig {
  currency: {
    symbol: string
    format: CurrencyFormat
    decimal: string
    thousand: string
    precision: number
  }
  number: {
    precision: number
    thousand: string
    decimal: string
  }
}

function currencyFormat (format: string): CurrencyFormat {
  return {
    pos: format,
    neg: `-${format}`,
    zero: format,
  }
}

export function Euro (symbol: string, space = true, after = true, precision = 2): AccountingConfig {
  return {
    currency: {
      symbol,
      format: after
        ? (space ? currencyFormat('%v %s') : currencyFormat('%v%s'))
        : (space ? currencyFormat('%s %v') : currencyFormat('%s%v')),
      decimal: ',',
      thousand: '.',
      precision,
    },
    number: {
      precision: 0,
      thousand: '.',
      decimal: ',',
    },
  }
}

export function Dollar (symbol: string, space = false, after = false, precision = 2): AccountingConfig {
  return {
    currency: {
      symbol,
      format: after
        ? (space ? currencyFormat('%v %s') : currencyFormat('%v%s'))
        : (space ? currencyFormat('%s %v') : currencyFormat('%s%v')),
      decimal: '.',
      thousand: ',',
      precision,
    },
    number: {
      precision: 0,
      thousand: ',',
      decimal: '.',
    },
  }
}

export function arrayMove<T> (arr: T[], old_index: number, new_index: number): T[] {
  if (new_index >= arr.length) {
    var k = new_index - arr.length + 1
    while (k--) {
      arr.push(undefined as T)
    }
  }
  arr.splice(new_index, 0, arr.splice(old_index, 1)[0])
  return arr // for testing
}

interface RootDimensions {
  height?: number
  width?: number
}

export function calculateHeight (root: RootDimensions, offset: number): string {
  const height = root.height || window.innerHeight || document.documentElement.clientHeight || document.body.clientHeight
  return `maxHeight: ${height - offset}px`
}

export function calculateWidth (root: RootDimensions, offset: number): string {
  const width = root.width || window.innerWidth || document.documentElement.clientWidth || document.body.clientWidth
  return `width: ${width - offset}px`
}

export function hourToMomentDate (hour: string | null | undefined): moment.Moment | null {
  return hour ? moment(`${moment().format(DATE_FORMAT)}T${hour}`) : null
}

export function blobDownloader (blob: Blob, fileName = 'booksy-file.pdf'): void {
  if (!(blob instanceof Blob)) {
    throw new Error('Blob has wrong format')
  }

  if (!fileName.includes('.pdf')) {
    throw new Error('Blob name is wrong')
  }

  const navigatorWithMsSave = window.navigator as Navigator & {
    msSaveOrOpenBlob?: (blob: Blob) => void
  }

  if (window.navigator && navigatorWithMsSave.msSaveOrOpenBlob) {
    navigatorWithMsSave.msSaveOrOpenBlob(blob)
    return
  }
  const data = window.URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = data
  link.download = fileName
  link.click()
  setTimeout(function () {
    window.URL.revokeObjectURL(data)
  }, 100)
}

export function isConfiguredBusiness (business: unknown): boolean {
  const businessId = path(['id'], business)
  const businessStatus = path(['status'], business)

  return Boolean(businessId && businessStatus !== BUSINESS_STATUSES.SETUP)
}

export function isPromise (object: unknown): boolean {
  try {
    return typeof (object as { then?: unknown }).then === 'function'
  } catch (e) {
    return false
  }
}

export function checkVersionOfMobileiOS (): number {
  const agent = window.navigator.userAgent
  const start = agent.indexOf('iPhone OS ')
  if (agent.indexOf('iPhone') > -1 && start > -1) {
    return Number(agent.substr(start + 10, 3).replace('_', '.'))
  }
  return 0
}

interface BrowserInfo {
  icon: string
  label: string
}

export const BROWSER: Record<string, BrowserInfo> = {
  EDGE: { icon: 'edge', label: 'Edge' },
  OPERA: { icon: 'opera', label: 'Opera' },
  CHROMIUM: { icon: 'chromium', label: 'Chromium' },
  CHROME: { icon: 'chrome', label: 'Chrome' },
  FIREFOX: { icon: 'firefox', label: 'Firefox' },
  SAFARI: { icon: 'safari', label: 'Safari' },
  BRAVE: { icon: 'brave', label: 'Brave' },
  SAMSUNG_INTERNET: { icon: 'samsung-internet', label: 'Samsung Internet' },
  VIVALDI: { icon: 'vivaldi', label: 'Vivaldi' },
  OTHER: { icon: 'web', label: 'Browser' },
}

interface BrowserResult extends BrowserInfo {
  version: string
}

export function getBrowser (): BrowserResult {
  const agent = window.navigator.userAgent.toLowerCase()

  let browser = BROWSER.OTHER
  let version: string | undefined = 'Unknown'

  if (/opr|opt/i.test(agent)) {
    browser = BROWSER.OPERA
    version = agent.match(/(?:opr|opt)\/(\d+(\.\d+)?)/)?.[1]
  } else if (/edg/i.test(agent)) {
    browser = BROWSER.EDGE
    version = agent.match(/edg\/(\d+(\.\d+)?)/)?.[1]
  } else if (/chrome|chromium|crios/i.test(agent)) {
    browser = BROWSER.CHROME
    version = agent.match(/(?:chrome|chromium|crios)\/(\d+(\.\d+)?)/)?.[1]
  } else if (/firefox|fxios/i.test(agent)) {
    browser = BROWSER.FIREFOX
    version = agent.match(/(?:firefox|fxios)\/(\d+(\.\d+)?)/)?.[1]
  } else if (/safari/i.test(agent)) {
    browser = BROWSER.SAFARI
    version = agent.match(/version\/(\d+(\.\d+)?)/)?.[1]
  }

  return {
    ...browser,
    version: version || 'Unknown',
  }
}

interface ToastLike {
  error: (message: string) => void
}

interface LoggerLike {
  error: (message: string | unknown) => void
}

interface ApiError {
  response?: {
    data?: {
      errors?: FormError[]
    }
  }
}

export function displayComposableErrorToast (error: ApiError, toast: ToastLike, logger: LoggerLike): void {
  const errors = error.response?.data?.errors
  const description = Array.isArray(errors) && errors[0]?.description
  const errorMessage = description || error

  toast.error(errorMessage as string)
  logger.error(errorMessage)
}

export function createThrottler (delay?: number | string): (action?: unknown) => void {
  let queue: unknown[] = []
  let timeout: ReturnType<typeof setTimeout> | null = null

  const onTimeout = () => {
    if (timeout) clearTimeout(timeout)
    timeout = null

    const lastAction = queue.pop()
    queue = []

    if (typeof lastAction === 'function') {
      lastAction()
    }
  }

  return function (action?: unknown) {
    queue.push(action)

    if (timeout) {
      return
    }

    timeout = setTimeout(onTimeout, +(delay ?? 0) || 0)
  }
}

export function transformKeysToCamelCase (obj: Record<string, unknown>): Record<string, unknown> {
  return Object.keys(obj).reduce<Record<string, unknown>>((acc, key) => {
    return {
      [camelCase(key)]: obj[key],
      ...acc,
    }
  }, {})
}

export function extractFirstDescriptionFromApiError (error: {
  response?: { data?: { errors?: Array<{ description?: string }> } }
  data?: { errors?: Array<{ description?: string }> }
} | null | undefined): string | null {
  const errors = error?.response?.data?.errors ?? error?.data?.errors
  if (errors && Array.isArray(errors)) {
    for (const error of errors) {
      if (error?.description) return error.description
    }
  }
  return null
}

export function extractApiErrorMessage (error: unknown): string | null {
  const data = (error as {
    response?: {
      data?: {
        detail?: unknown
        errors?: unknown
      }
    }
  })?.response?.data

  if (typeof data?.detail === 'string') {
    return data.detail
  }

  const errors = data?.errors
  if (Array.isArray(errors)) {
    for (const item of errors) {
      if (Array.isArray(item)) {
        const messages = item.filter((part): part is string => typeof part === 'string')
        const message = messages[messages.length - 1]

        if (message) {
          return message
        }
      }
    }
  }

  return extractFirstDescriptionFromApiError(error as {
    response?: { data?: { errors?: Array<{ description?: string }> } }
  })
}

export function transformCamelCaseToSnakeCase (str: string): string {
  return str.replace(/([A-Z])/g, '_$1').toLowerCase()
}
